/**
 * AnimatedCount Component
 * 
 * Slot machine style number animation.
 * Digits slide up when increasing, down when decreasing.
 */

import { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

type Direction = 'up' | 'down' | null;

const digitVariants = {
  enter: (direction: Direction) => ({ y: direction === 'up' ? '100%' : '-100%' }),
  center: { y: '0%' },
  exit: (direction: Direction) => ({ y: direction === 'up' ? '-100%' : '100%' }),
};

interface AnimatedDigitProps {
  digit: string;
  direction: Direction;
}

/**
 * AnimatedDigit - Single digit with slot machine animation
 */
const AnimatedDigit = memo(function AnimatedDigit({ 
  digit, 
  direction 
}: AnimatedDigitProps) {
  return (
    <span className="relative inline-block h-[1lh] w-[1ch] shrink-0 overflow-hidden">
      <AnimatePresence initial={false} mode="sync" custom={direction}>
        <motion.span
          key={digit}
          custom={direction}
          variants={digitVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            mass: 0.8
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {digit}
        </motion.span>
      </AnimatePresence>
    </span>
  );
});

interface AnimatedCountProps {
  value: number;
  className?: string;
}

/**
 * AnimatedCount - Slot machine style number animation
 * 
 * @example
 * <AnimatedCount value={count} />
 */
export function AnimatedCount({ value, className }: AnimatedCountProps) {
  const [previousValue, setPreviousValue] = useState(value);
  const [direction, setDirection] = useState<Direction>(null);

  if (value !== previousValue) {
    setPreviousValue(value);
    setDirection(value > previousValue ? 'up' : 'down');
  }
  
  const digits = String(value).split('');
  
  return (
    <span className={cn('inline-flex items-center justify-center tabular-nums', className)}>
      <span className="sr-only">{value}</span>
      <span aria-hidden="true" className="inline-flex">
        {digits.map((digit, i) => (
          <AnimatedDigit
            key={i}
            digit={digit}
            direction={direction}
          />
        ))}
      </span>
    </span>
  );
}
